import React, { useState } from 'react'
import PropTypes from 'prop-types'
import FormHelperText from '@material-ui/core/FormHelperText'
import { getErrorMessage, inputHasError } from '../../utils/formErrorUtils'
import { makeStyles } from '@material-ui/core'
import VisaCardLogo from '../../icons/VisaCardLogo'
import ColorDiscover from '../Base/Icons/PaymentMethods/ColorDiscover'
import ColorMasterCard from '../Base/Icons/PaymentMethods/ColorMasterCard'
import ColorAmex from '../Base/Icons/PaymentMethods/ColorAmex'

const useStyles = makeStyles((theme) => ({
    inputGroupContainer: {
        width: '100%',
    },
    inputContainer: {
        display: 'block',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
    },
    halfWidthField: {
        width: '48%',
    },
    inputLabel: {
        position: 'absolute',
        padding: '1rem 0',
        top: '50%',
        left: '25px',
        right: 30,
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease, background-color 0.2s ease', // Add background-color transition
        pointerEvents: 'none',
        backgroundColor: 'white', // Add background color
        color: '#BBB',
        zIndex: 1, // Ensure the label is above the input
    },
    labelFloating: {
        top: '21px',
        fontSize: '12px',
        color: '#BBB',
        padding: '1rem 0',
        backgroundColor: 'transparent', // Set background color to be semi-transparent
    },
    input: {
        fontSize: '1rem',
        color: '#222',
        display: 'block',
        height: '66px',
        border: '2px solid #8a8a8a',
        borderRadius: '8px',
        paddingTop: '1.2rem',
        paddingRight: '1.5rem',
        paddingLeft: '1.5rem',
        marginLeft: 0,
        letterSpacing: '.025em',
        background: 'white',
        boxShadow:
            '0 1px 3px rgba(50,50,93,.14902), 0 1px 0 rgba(0,0,0,.0196078)',
        width: '100%',
        '&::placeholder': {
            color: '#757575',
            fontSize: '1rem',
            opacity: 0.5,
        },
        backgroundColor: 'white', // Ensure background is white
        '&:focus': {
            backgroundColor: 'white', // Ensure background remains white on focus
            outline: 'unset',
        },
        '&.error': {
            borderColor: '#D74242',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1rem',
        },
    },
    select: {
        backgroundColor: 'white',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 140 140' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='20,50 70,100 120,50' fill='none' stroke='%23666' stroke-width='15' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '12px',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        '&:focus': {
            outline: 'none',
        },
        '&.error': {
            borderColor: '#D74242',
        },
    },
    selectIcon: {
        position: 'absolute',
        top: '1.7rem',
        right: '1rem',
        pointerEvents: 'none',
    },
    errorHelperText: {
        fontSize: '0.75rem',
        color: '#D74242',
        marginBottom: '0.5rem',
    },
    cardIcon: {
        position: 'absolute',
        width: '35px',
        zIndex: '1',
        height: '20px',
        right: '15px',
        top: '25px',
        '& svg': {
            height: '20px',
            width: '35px',
        },
    },
    stripeElement: {
        paddingTop: '1.8rem',
    },
}))

const FormField = ({
    label,
    id,
    type,
    required,
    autoComplete,
    value,
    onChange,
    onBlur,
    onFocus,
    disabled,
    errors,
    maxLength,
    style,
    halfWidthField,
    showLogo,
    cardType,
    mask,
    options,
    pattern,
    stripeElement,
}) => {
    const classes = useStyles()
    const [isFocused, setIsFocused] = useState(false)
    const [stripeElementEmpty, setStripeElementEmpty] = useState(false)

    const returnCardLogo = (type) => {
        switch (type) {
            case 'visa':
                return <VisaCardLogo />
            case 'discover':
                return <ColorDiscover />
            case 'american-express':
                return <ColorAmex />
            case 'mastercard':
                return <ColorMasterCard />
            default:
                return null
        }
    }

    const getOptions = () => {
        let optionsArray = []
        optionsArray.push(<option value={''} disabled></option>)
        for (const label in options) {
            optionsArray.push(
                <option value={options[label]} key={options[label]}>
                    {label}
                </option>
            )
        }
        return optionsArray
    }

    const getInput = () => {
        const errorClass = errors && getErrorMessage(errors, id) ? 'error' : ''

        if (stripeElement) {
            return React.cloneElement(stripeElement, {
                className: `${classes.input} ${classes.stripeElement} ${errorClass}`,
                onFocus: () => {
                    setIsFocused(true)
                    onFocus && onFocus()
                },
                onBlur: () => {
                    if (stripeElementEmpty) {
                        setIsFocused(false)
                    }
                    onBlur && onBlur()
                },
                onChange: (event) => {
                    setStripeElementEmpty(event.empty)
                    onChange && onChange(event)
                },
            })
        }

        if (!options) {
            return (
                <input
                    className={`${classes.input} ${
                        mask ? 'fs-mask' : ''
                    } ${errorClass}`}
                    id={id}
                    type={type}
                    required={required}
                    autoComplete={autoComplete}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onFocus={() => {
                        setIsFocused(true)
                        onFocus && onFocus()
                    }}
                    onBlurCapture={() => {
                        setIsFocused(false)
                    }}
                    disabled={disabled}
                    maxLength={maxLength || null}
                    pattern={pattern}
                />
            )
        }

        return (
            <select
                className={`${classes.input} ${classes.select} ${
                    mask ? 'fs-mask' : ''
                } ${errorClass}`}
                id={id}
                key={id}
                required={required}
                autoComplete={autoComplete}
                value={value}
                onChange={onChange}
                onFocus={() => {
                    setIsFocused(true)
                    onFocus && onFocus()
                }}
                onBlurCapture={() => {
                    setIsFocused(false)
                }}
                disabled={disabled}
            >
                {getOptions()}
            </select>
        )
    }

    return (
        <div
            className={`${classes.inputGroupContainer} ${
                halfWidthField ? classes.halfWidthField : ''
            }`}
        >
            <div className={`${classes.inputContainer}`} style={style}>
                <label
                    htmlFor={id}
                    className={`${classes.inputLabel} ${
                        value || isFocused ? classes.labelFloating : ''
                    }`}
                >
                    {label}
                </label>
                {getInput()}
                {showLogo && (
                    <div className={classes.cardIcon}>
                        {returnCardLogo(cardType)}
                    </div>
                )}
            </div>
            {errors && getErrorMessage(errors, id) && (
                <FormHelperText
                    error={inputHasError(errors, id)}
                    className={classes.errorHelperText}
                >
                    {getErrorMessage(errors, id)}
                </FormHelperText>
            )}
        </div>
    )
}

FormField.propTypes = {
    label: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    required: PropTypes.bool,
    autoComplete: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    disabled: PropTypes.bool,
    errors: PropTypes.object,
    maxLength: PropTypes.number,
    style: PropTypes.object,
    halfWidthField: PropTypes.bool,
    showLogo: PropTypes.bool,
    cardType: PropTypes.string,
    mask: PropTypes.bool,
    options: PropTypes.object,
    pattern: PropTypes.string,
    stripeElement: PropTypes.element,
}

export default FormField
